import type { PostGroupEntry } from '../types/inbox';
import type { InboxThreadView } from '../types/inboxView';

// Community Inbox Part B — PURE display-nesting builder. Resolution is PER
// COMMENT (every customer comment, top-level or reply, is its own resolvable
// thread); this only decides how they're INDENTED for context, mirroring
// Facebook's structure: a reply renders under the comment it replies to.
//
// Orphans (a reply whose parent isn't in the group — e.g. the parent was
// deleted on the platform, or is outside the fetched window) are promoted to
// top level rather than dropped, so nothing ever disappears from the view.

// Generic over the entry so the staff page (stored docs) and the club portal
// (its projection) share ONE tree builder. It reads only these three fields.
export interface TreeEntry {
  thread: Pick<InboxThreadView, 'platformCommentId' | 'parentCommentId' | 'createdAt'>;
}

export interface CommentNode<E extends TreeEntry = PostGroupEntry> {
  entry: E;
  depth: number;
  children: CommentNode<E>[];
}

/** Max indent depth — deeper replies flatten to keep the pane readable. */
export const MAX_COMMENT_DEPTH = 3;

export function buildCommentTree<E extends TreeEntry>(entries: E[]): CommentNode<E>[] {
  const byCommentId = new Map<string, E>();
  for (const e of entries) {
    const id = e.thread.platformCommentId;
    if (id) byCommentId.set(id, e);
  }

  const nodes = new Map<string, CommentNode<E>>();
  const roots: CommentNode<E>[] = [];

  // Stable order: oldest comment first (the caller sorts, but don't rely on it).
  const ordered = [...entries].sort((a, b) =>
    a.thread.createdAt.localeCompare(b.thread.createdAt),
  );

  for (const entry of ordered) {
    const node: CommentNode<E> = { entry, depth: 0, children: [] };
    const key = entry.thread.platformCommentId;
    if (key) nodes.set(key, node);
  }

  for (const entry of ordered) {
    const key = entry.thread.platformCommentId;
    const node = key ? nodes.get(key) : undefined;
    if (!node) {
      // No comment id (legacy per-post thread) → top level.
      roots.push({ entry, depth: 0, children: [] });
      continue;
    }
    const parentId = entry.thread.parentCommentId ?? null;
    const parent = parentId && byCommentId.has(parentId) ? nodes.get(parentId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node); // top-level, or an orphaned reply (promoted)
  }

  // Assign depths after the tree is wired (cap the indent).
  const assign = (list: CommentNode<E>[], depth: number): void => {
    for (const n of list) {
      n.depth = Math.min(depth, MAX_COMMENT_DEPTH);
      assign(n.children, depth + 1);
    }
  };
  assign(roots, 0);
  return roots;
}

/** Depth-first flatten — the render order for the post-group list. */
export function flattenCommentTree<E extends TreeEntry>(roots: CommentNode<E>[]): CommentNode<E>[] {
  const out: CommentNode<E>[] = [];
  const walk = (list: CommentNode<E>[]): void => {
    for (const n of list) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(roots);
  return out;
}
